import { showToast } from '../../../../utils/toast.util';
import React, { useEffect, useMemo } from 'react';
import { useProfile } from '../profile.context';
import { Controller, Form, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Row } from '../../../../../components/ui';
import { AvatarWrapper } from '../../../register/RegisterForm.style';
import { AvatarComponent } from '../../horarios/components/avatar.component';
import { ProfileFormColumn, ProfileFormFooter, ProfileFormRow } from './ProfileForm.syle';
import { ButtonStyled, InputMaskStyled, InputStyled, SelectStyled } from '../../../../elements/forms/InputContainer';
import { useMutation } from '@apollo/client';
import { CREATE_OR_UPDATE_USER_MUTATION } from '../../mutations/user.mutation';
import { LoadingContent } from '../../../../components';
import { DirectUpload } from '../../../../components/media/DirectUpload';

declare global {
  interface Window {
    M: {
      toast: (options: { html: string }) => void;
    };
  }
}

const schema = z.object({
  name: z.string().min(1),
  lastName: z.string().min(1),
  email: z.string().email(),
  phone: z.string().min(1).nullish(),
  status: z.string().min(1).nullish(),
  cpf: z.string().nullish(),
  gender: z.enum(['Masculino', 'Feminino', '-']).nullish(),
  birthdate: z.string().nullish(),
  gympassId: z.string().nullish(),
  height: z.string().min(1).nullish(),
})

const STATUS_OPTIONS = [
  {label: 'Lead', value: 'lead'},
  {label: 'Ativo', value: 'ativo'},
  {label: 'Pendente', value: 'pendente'},
  {label: 'Cancelado', value: 'cancelado'},
  {label: 'Inativo', value: 'inativo'},
]

export const ProfileEdit = () => {
  const { account, aluno } = useProfile();
  const {control, handleSubmit, formState: {errors}, reset, setValue} = useForm({
    resolver: zodResolver(schema)
  });
  
  const [avatarUrl, setAvatarUrl] = React.useState<string | null>(null);
  const [createOrUpdateUser, {loading, data, error}] = useMutation(CREATE_OR_UPDATE_USER_MUTATION);

  const alunoValues = useMemo(() => {
    return {
      ...aluno,
      status: aluno?.relationship?.status
    }
  }, [aluno])

  useEffect(() => {
    if(alunoValues){
      reset(alunoValues)
    }
  }, [alunoValues]);

  const onSubmit = (data: any) => {
    const variables = {
      ...data,
      origin: 'panel',
      avatarUrl: avatarUrl
    }
    
    createOrUpdateUser({
      variables,
    })
  }

  const handleUploadComplete = (fileUrl: string) => {
    setAvatarUrl(fileUrl);
  };

  useEffect(() => {
    if(data && data.createOrUpdateUser){
      window.showToast({html: 'Perfil atualizado com sucesso'});
      const user = data.createOrUpdateUser.user;
      window.location.href = `/${account.code}/admin/alunos/${user.id}`;
    }
  }, [data]);

  useEffect(() => {
    if(error){
      window.showToast({html: 'Erro ao atualizar o perfil'});
    }
  }, [error]);

  if(loading) return <LoadingContent loading={loading} />

  return (
    <div>
      <Row>
        <div className='flow-text' style={{fontWeight: 600}}>{aluno?.id ? 'Editar Perfil' : 'Criar Perfil'}</div>
      </Row>
      <Row>
        <Form control={control} onSubmit={handleSubmit(onSubmit)}>
          <ProfileFormRow>
            <ProfileFormColumn>
                <AvatarWrapper>
                  <DirectUpload 
                    onUploadComplete={handleUploadComplete}
                    mediaType='image'
                    folder='avatars'
                    cropAspect={1}
                    cropTitle='Ajustar foto de perfil'
                  >
                    <AvatarComponent aluno={aluno} size={230} hideName={true} noLink />
                  </DirectUpload>
                <Row>
                  <em>Clique ou arraste uma imagem para escolher uma nova foto de perfil</em>
                </Row>
              </AvatarWrapper>
            <Row style={{width: '100%'}}>
              <Controller
                name="status"
                control={control}
                render={({field}) => (
                  <SelectStyled
                    {...field}
                    s={12}
                    label='Status'
                    options={STATUS_OPTIONS}
                    value={STATUS_OPTIONS.find(option => option.value === field.value)}
                    onChange={({value} : {value: string}) => field.onChange(value)}
                    className={errors.status ? 'invalid' : ''}
                    error={errors.status?.message}
                  />
                )}
              />
            </Row>
            </ProfileFormColumn>
            <ProfileFormColumn style={{flex: 2}}>
              <Controller
                name="name"
                control={control}
                render={({field}) => (
                  <InputStyled 
                    {...field} 
                    s={12}
                    label='Nome'
                    placeholder='Nome'
                    className={errors.name ? 'invalid' : ''}
                    error={errors.name?.message}
                  />
                )}
              />
              <Controller
                name="lastName"
                control={control}
                render={({field}) => (
                  <InputStyled 
                    {...field} 
                    s={12}
                    label='Sobrenome'
                    placeholder='Sobrenome'
                    className={errors.lastName ? 'invalid' : ''}
                    error={errors.lastName?.message}
                  />
                )}
              />
              <Controller
                name="email"
                control={control}
                render={({field}) => (
                  <InputStyled 
                    {...field} 
                    s={12}
                    label='Email'
                    type='email'
                    placeholder='Email'
                    className={errors.email ? 'invalid' : ''}
                    error={errors.email?.message}
                  />
                )}
              />
              <Controller
                name="phone"
                control={control}
                render={({field}) => {
                  let value = String(field.value).match(/[0-9]+/g)?.join('');
                  if(!value?.length) {
                    value = '55'
                  }
                  return(
                  <InputMaskStyled 
                    {...field} 
                    s={12}
                    label='Telefone'
                    mask={'+99 (99) 99999-9999'}
                    type='tel'
                    value={value}
                    placeholder='Telefone'
                    className={errors.phone ? 'invalid' : ''}
                    error={errors.phone?.message}
                  />
                )}}
              />
              <Controller
                name="cpf"
                control={control}
                render={({field}) => (
                  <InputMaskStyled 
                    {...field} 
                    s={12}
                    label='CPF'
                    type='tel'
                    mask={'999.999.999-99'}
                    placeholder='CPF'
                    className={errors.cpf ? 'invalid' : ''}
                    error={errors.cpf?.message}
                  />
                )}
              />
              <Controller
                name="gympassId"
                control={control}
                render={({field}) => (
                  <InputStyled 
                    {...field} 
                    s={12}
                    type='tel'
                    label='Gympass ID'
                    placeholder='Gympass ID'
                    className={errors.gympassId ? 'invalid' : ''}
                    error={errors.gympassId?.message}
                  />
                )}
              />
              <ProfileFormFooter style={{flexDirection: 'column'}}>
                <ButtonStyled type='submit'>{aluno?.id ? 'Salvar' : 'Criar'}</ButtonStyled>
                <a onClick={() => {
                  window.history.back();
                  setTimeout(() => {
                    window.location.reload()
                  }, 500)
                  }} style={{color: 'red'}}>Cancelar</a>
              </ProfileFormFooter>
            </ProfileFormColumn>
          </ProfileFormRow>
        </Form>
      </Row>
    </div>
  )
}